import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../api/api';
import {
  ArrowLeft, Star, MapPin, Briefcase, Clock, Envelope, Phone,
  SpinnerGap, CheckCircle, XCircle, UserCircle, Tag, Heart, ChatCircle
} from '@phosphor-icons/react';
import { toast } from '../hooks/use-toast';

const BidComparison = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/login?type=contractor'); return; }
    (async () => {
      try {
        const res = await axiosInstance.get(`/bid-comparison/${jobId}`);
        if (res.data?.success) setData(res.data);
      } catch (err) {
        toast({ title: 'Error loading bids', description: err.response?.data?.detail || 'Unknown error', variant: 'destructive' });
      } finally { setLoading(false); }
    })();
  }, [jobId, navigate]);

  const toggleSelect = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : prev.length < 4 ? [...prev, id] : prev);
  };

  const filteredBids = selected.length > 0 ? data?.bids?.filter(b => selected.includes(b.application_id)) : data?.bids;

  if (loading) return (
    <div className="min-h-screen bg-[#F0F0F2] dark:bg-neutral-900 flex items-center justify-center">
      <SpinnerGap size={48} weight="bold" className="animate-spin text-[#2B4C6F]" />
    </div>
  );

  if (!data) return (
    <div className="min-h-screen bg-[#F0F0F2] dark:bg-neutral-900 flex items-center justify-center">
      <p className="text-neutral-500">No data found</p>
    </div>
  );

  const { job, bids } = data;

  return (
    <div className="min-h-screen bg-[#F0F0F2] dark:bg-neutral-900 pb-24" data-testid="bid-comparison-page">
      {/* Header */}
      <div className="bg-neutral-900 dark:bg-black border-b-4 border-[#2B4C6F]">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-neutral-400 hover:text-white mb-3 transition-colors" data-testid="back-btn">
            <ArrowLeft size={20} weight="bold" /> Back
          </button>
          <h1 className="text-2xl sm:text-3xl font-black text-white">Compare Bids</h1>
          <p className="text-neutral-400 mt-1">{job.title} — {bids.length} application{bids.length !== 1 ? 's' : ''}</p>
          {job.budget_min > 0 && (
            <p className="text-sm text-[#2B4C6F] font-bold mt-1">Budget: ${job.budget_min.toLocaleString()} — ${job.budget_max.toLocaleString()}</p>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Selection filter */}
        {bids.length > 1 && (
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <span className="text-xs font-bold uppercase text-neutral-500">Filter:</span>
            {bids.map(b => (
              <button key={b.application_id} onClick={() => toggleSelect(b.application_id)}
                className={`px-3 py-1 text-xs font-bold rounded-lg border-2 transition-all ${
                  selected.includes(b.application_id)
                    ? 'bg-[#2B4C6F] text-white border-[#2B4C6F]'
                    : 'bg-white dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 border-neutral-300 dark:border-neutral-600 hover:border-[#2B4C6F]'
                }`} data-testid={`filter-bid-${b.application_id}`}>
                {b.worker?.name || 'Unknown'}
              </button>
            ))}
            {selected.length > 0 && (
              <button onClick={() => setSelected([])} className="px-3 py-1 text-xs font-bold text-red-500 hover:underline">Clear</button>
            )}
          </div>
        )}

        {bids.length === 0 ? (
          <div className="text-center py-16">
            <Briefcase size={64} weight="bold" className="text-neutral-300 dark:text-neutral-600 mx-auto mb-4" />
            <h3 className="text-xl font-black text-neutral-700 dark:text-neutral-300">No Applications Yet</h3>
          </div>
        ) : (
          <div className={`grid gap-4 ${filteredBids.length === 1 ? 'grid-cols-1 max-w-lg' : filteredBids.length === 2 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3'}`} data-testid="bids-grid">
            {filteredBids.map((bid) => {
              const w = bid.worker;
              const s = bid.stats;
              const isAccepted = bid.status === 'accepted';
              const isPending = bid.status === 'pending';

              return (
                <div key={bid.application_id}
                  className={`bg-white dark:bg-neutral-800 border-2 rounded-xl overflow-hidden shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F] ${
                    isAccepted ? 'border-emerald-500' : 'border-black dark:border-neutral-700'
                  }`}
                  data-testid={`bid-card-${bid.application_id}`}>

                  {/* Status banner */}
                  {isAccepted && (
                    <div className="bg-emerald-500 text-white text-xs font-black text-center py-1 uppercase tracking-wider flex items-center justify-center gap-1">
                      <CheckCircle size={14} weight="bold" /> Hired
                    </div>
                  )}
                  {bid.status === 'rejected' && (
                    <div className="bg-red-500 text-white text-xs font-black text-center py-1 uppercase tracking-wider flex items-center justify-center gap-1">
                      <XCircle size={14} weight="bold" /> Declined
                    </div>
                  )}

                  <div className="p-5">
                    {/* Worker header */}
                    <div className="flex items-start gap-3 mb-4">
                      <div className="flex-shrink-0">
                        {w?.profile_image ? (
                          <img src={w.profile_image} alt={w.name} className="w-14 h-14 rounded-lg border-2 border-black object-cover" />
                        ) : (
                          <div className="w-14 h-14 rounded-lg border-2 border-black bg-[#2B4C6F] flex items-center justify-center">
                            <UserCircle size={32} weight="bold" className="text-white" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-black text-neutral-900 dark:text-white text-lg truncate">{w?.name || 'Unknown'}</h3>
                          {s?.is_favorite && <Heart size={16} weight="fill" className="text-[#D95800] flex-shrink-0" />}
                        </div>
                        {w?.location && <p className="text-xs text-neutral-500 flex items-center gap-1"><MapPin size={12} weight="bold" /> {w.location}</p>}
                      </div>
                    </div>

                    {/* Rate */}
                    <div className="bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-300 dark:border-amber-700 rounded-lg p-3 mb-4 text-center">
                      <p className="text-xs font-bold uppercase text-amber-600 dark:text-amber-400">Proposed Rate</p>
                      <p className="text-xl font-black text-amber-800 dark:text-amber-300">{bid.proposed_rate || 'N/A'}</p>
                    </div>

                    {/* Stats grid */}
                    <div className="grid grid-cols-2 gap-2 mb-4">
                      <div className="bg-neutral-50 dark:bg-neutral-700/50 rounded-lg p-2 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Star size={14} weight="fill" className="text-amber-500" />
                          <span className="font-black text-neutral-900 dark:text-white">{s?.avg_rating || '—'}</span>
                        </div>
                        <p className="text-[10px] text-neutral-500 uppercase">{s?.review_count || 0} reviews</p>
                      </div>
                      <div className="bg-neutral-50 dark:bg-neutral-700/50 rounded-lg p-2 text-center">
                        <span className="font-black text-neutral-900 dark:text-white">{s?.total_completed_jobs || 0}</span>
                        <p className="text-[10px] text-neutral-500 uppercase">Jobs Done</p>
                      </div>
                      <div className="bg-neutral-50 dark:bg-neutral-700/50 rounded-lg p-2 text-center">
                        <span className="font-black text-neutral-900 dark:text-white">{s?.jobs_with_you || 0}</span>
                        <p className="text-[10px] text-neutral-500 uppercase">With You</p>
                      </div>
                      <div className="bg-neutral-50 dark:bg-neutral-700/50 rounded-lg p-2 text-center">
                        <span className="font-black text-neutral-900 dark:text-white">{w?.experience_years || '—'}</span>
                        <p className="text-[10px] text-neutral-500 uppercase">Yrs Exp</p>
                      </div>
                    </div>

                    {/* Skills */}
                    {w?.skills?.length > 0 && (
                      <div className="mb-4">
                        <p className="text-[10px] font-bold uppercase text-neutral-500 mb-1.5">Skills</p>
                        <div className="flex flex-wrap gap-1">
                          {w.skills.map(skill => {
                            const isMatch = job.required_skills?.includes(skill);
                            return (
                              <span key={skill} className={`px-2 py-0.5 text-xs font-bold rounded border flex items-center gap-1 ${
                                isMatch
                                  ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border-emerald-300 dark:border-emerald-700'
                                  : 'bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300 border-neutral-200 dark:border-neutral-600'
                              }`}>
                                {isMatch && <CheckCircle size={10} weight="bold" />}
                                <Tag size={10} weight="bold" /> {skill}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Cover letter */}
                    <div className="mb-4">
                      <p className="text-[10px] font-bold uppercase text-neutral-500 mb-1">Cover Letter</p>
                      <p className="text-xs text-neutral-600 dark:text-neutral-400 leading-relaxed line-clamp-4">{bid.cover_letter}</p>
                    </div>

                    {/* Contact */}
                    <div className="flex flex-wrap gap-2 text-xs text-neutral-500">
                      {w?.email && <span className="flex items-center gap-1"><Envelope size={12} weight="bold" /> {w.email}</span>}
                      {w?.phone && <span className="flex items-center gap-1"><Phone size={12} weight="bold" /> {w.phone}</span>}
                    </div>

                    {/* Applied date */}
                    <p className="text-[10px] text-neutral-400 mt-2 flex items-center gap-1">
                      <Clock size={10} weight="bold" /> Applied {new Date(bid.applied_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default BidComparison;
